import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BUSINESS_INFO } from '@/constants/businessInfo';

interface SoftwareApplicationSchemaProps {
  /** Optional override for the current page URL used in the breadcrumb trail. */
  currentUrl?: string;
}

const PATH_LABELS: Record<string, string> = {
  login: 'Sign in',
  landing: 'Welcome',
  signup: 'Sign up',
  faq: 'FAQ',
  privacy: 'Privacy policy',
  terms: 'Terms of use',
  'delete-account-info': 'Delete account',
  'manage-subscription': 'Manage subscription',
  account: 'Account',
  'subscription-success': 'Subscription confirmed',
  'ask-guiri': 'Ask Guiri',
  profile: 'Profile',
  history: 'History',
  barrio: 'Mi barrio',
  guides: 'Guides',
};

const labelFor = (segment: string): string => {
  if (PATH_LABELS[segment]) return PATH_LABELS[segment];
  // Locale code (e.g. /faq/es) → uppercase
  if (segment.length === 2 && /^[a-z]{2}$/i.test(segment)) return segment.toUpperCase();
  // Fallback: replace dashes, title-case
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Emits a BreadcrumbList JSON-LD for every non-home route.
 *
 * The site-wide SoftwareApplication/WebSite/Organization JSON-LD lives in
 * index.html so it ships as static HTML and is visible to crawlers that
 * don't execute JavaScript (Perplexity, ChatGPT user-agent fetch).
 */
const SoftwareApplicationSchema = ({ currentUrl }: SoftwareApplicationSchemaProps) => {
  const location = useLocation();

  if (location.pathname === '/') {
    return null;
  }

  const origin =
    typeof window !== 'undefined' ? window.location.origin : BUSINESS_INFO.url;
  const resolvedUrl = currentUrl || `${origin}${location.pathname}`;

  const segments = location.pathname.split('/').filter(Boolean);

  const itemListElement = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${origin}/`,
    },
    ...segments.map((segment, index) => {
      const path = '/' + segments.slice(0, index + 1).join('/');
      return {
        '@type': 'ListItem',
        position: index + 2,
        name: labelFor(segment),
        item: `${origin}${path}`,
      };
    }),
  ];

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>
      <link rel="canonical" href={resolvedUrl} />
    </Helmet>
  );
};

export default SoftwareApplicationSchema;
